import { ObjectUtils } from '@/commons/utils/objects'
import { VuexRegisterGlobals } from '@/store/decorators/vuex.register.globals'
import {
    BaseError,
    UserCredentials,
    AuthenticationError
} from '@/store/entities'
import {
    Module,
    Action,
    VuexModule
} from 'vuex-class-modules'

const AUTHENTICATION_TOKEN_KEY = 'user-credentials'

@VuexRegisterGlobals({
    actionParams: [{
        targetActionName: 'clearAll',
        contextActionName: 'clear'
    }]
})
@Module({ generateMutationSetters: true })
export default class Authentication extends VuexModule {
    userCredentials: string | null = sessionStorage.getItem(AUTHENTICATION_TOKEN_KEY) || null

    get isAuthenticated (): boolean {
        return !!(this.userCredentials)
    }

    getUserCredentials (): UserCredentials {
        if (!this.userCredentials) {
            return new UserCredentials()
        }
        return this.getJson(this.userCredentials)
    }

    resolveAuthorizationToken (): string {
        if (!this.userCredentials) {
            throw new AuthenticationError()
        }
        return this.getJson(this.userCredentials).token
    }

    isValidUserCredentials (): boolean | BaseError {
        const inSession = sessionStorage.getItem(AUTHENTICATION_TOKEN_KEY) || null
        if (inSession === null && this.userCredentials === null) {
            return false
        }

        this.assertUserCredentialsInitialized(inSession, this.userCredentials)

        const inSessionStorageUserCredentials = this.getJson(inSession)
        const inStorageUserCredentials = this.getJson(this.userCredentials)
        const expectedUserCredentialStruct = new UserCredentials() as UserCredentials & Record<string, string>

        this.assertUserCredentialsDataIntegrity(
            inSessionStorageUserCredentials, inStorageUserCredentials, expectedUserCredentialStruct
        )

        this.assertUserCredentialsEquality(inSessionStorageUserCredentials, inStorageUserCredentials)

        return true
    }

    private assertUserCredentialsInitialized (
        inSessionStorage: string | null,
        inStore: string | null
    ): void {
        if (inSessionStorage === null && inStore !== null) {
            throw new AuthenticationError()
        }

        if (inSessionStorage !== null && inStore === null) {
            throw new AuthenticationError()
        }
    }

    private getJson (userCredentials: string | null): UserCredentials & Record<string, string> {
        if (!userCredentials) {
            throw new AuthenticationError()
        }
        try {
            return JSON.parse(userCredentials)
        } catch (e) {
            throw new AuthenticationError()
        }
    }

    private assertUserCredentialsDataIntegrity (
        inSessionStorageUserCredentials: UserCredentials & Record<string, string>,
        inStorageUserCredentials: UserCredentials & Record<string, string>,
        expectedUserCredentialStruct: UserCredentials & Record<string, string>
    ): void {
        if (!ObjectUtils.isEqualObjectStruct(inStorageUserCredentials, expectedUserCredentialStruct)) {
            throw new AuthenticationError()
        }
        if (!ObjectUtils.isEqualObjectStruct(inSessionStorageUserCredentials, expectedUserCredentialStruct)) {
            throw new AuthenticationError()
        }
    }

    private assertUserCredentialsEquality (
        inSessionStorageUserCredentials: UserCredentials & Record<string, string>,
        inStorageUserCredentials: UserCredentials & Record<string, string>
    ): void {
        for (const propertyKey of Object.keys(inSessionStorageUserCredentials)) {
            if (!Object.prototype.hasOwnProperty.call(inSessionStorageUserCredentials, propertyKey)) {
                throw new AuthenticationError()
            }
            if (!Object.prototype.hasOwnProperty.call(inStorageUserCredentials, propertyKey)) {
                throw new AuthenticationError()
            }
            if (inSessionStorageUserCredentials[propertyKey] !== inStorageUserCredentials[propertyKey]) {
                throw new AuthenticationError()
            }
        }
    }

    @Action
    saveUserCredentials (userCredentials: UserCredentials): void {
        sessionStorage.setItem(AUTHENTICATION_TOKEN_KEY, JSON.stringify(userCredentials))
        this.userCredentials = JSON.stringify(userCredentials)
    }

    @Action
    clear (): void {
        sessionStorage.removeItem(AUTHENTICATION_TOKEN_KEY)
        this.userCredentials = null
    }
}
